package dev.lingvo.site.components.studio.editor

import dev.lingvo.site.components.studio.canvas.BitmapImage
import kotlin.js.Promise
import kotlin.math.ceil
import kotlin.math.min
import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.files.Blob

/** Captures decode once per content identity, downsampled to at most twice their on-screen dimensions. */
internal class CaptureImageCache(private val maxBytes: Long = 32L * 1024 * 1024) {
    private val images = linkedMapOf<String, BitmapImage>()
    private var generation = 0
    private var bytes = 0L
    val size: Int get() = images.size
    suspend fun load(key: String, blob: Blob, nativeWidth: Int, nativeHeight: Int, cssWidth: Double, cssHeight: Double): BitmapImage {
        images[key]?.let { return it }
        require(nativeWidth > 0 && nativeHeight > 0 && cssWidth.isFinite() && cssHeight.isFinite() && cssWidth > 0 && cssHeight > 0)
        val scale = min(1.0, min(2 * cssWidth / nativeWidth, 2 * cssHeight / nativeHeight))
        val options: dynamic = js("({resizeQuality:'high'})")
        val width = ceil(nativeWidth * scale).toInt().coerceAtLeast(1)
        val height = ceil(nativeHeight * scale).toInt().coerceAtLeast(1)
        options.resizeWidth = width
        options.resizeHeight = height
        require(width.toLong() * height * 4 <= maxBytes) { "Capture decode exceeds the editor bitmap budget" } // i18n-ignore: internal contract diagnostic
        val token = generation
        val bitmap = window.asDynamic().createImageBitmap(blob, options).unsafeCast<Promise<dynamic>>().await()
        if (generation != token) { bitmap.close(); throw kotlinx.coroutines.CancellationException("The editor detached during capture decoding") } // i18n-ignore: internal contract diagnostic
        images[key]?.let { bitmap.close(); return it }
        val image = BitmapImage(key, bitmap)
        val size = image.width.toLong() * image.height * 4
        while (images.isNotEmpty() && bytes + size > maxBytes) {
            val removed = images.remove(images.keys.first())!!
            bytes -= removed.width.toLong() * removed.height * 4; removed.close()
        }
        images[key] = image; bytes += size
        return image
    }
    fun get(key: String): BitmapImage? = images[key]
    fun clear() { generation++; images.values.forEach { it.close() }; images.clear(); bytes = 0 }
}
